/**
 * Ticket Form Page
 * ================
 * Create or edit a ticket. Supports ?asset_id= query param for asset pre-fill.
 * Supports file attachments (upload after ticket creation, or add/remove on edit).
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Save, Upload, X, FileText, Image, FileSpreadsheet, Archive, File as FileIcon } from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/Card'
import Button from '@/components/Button'
import {
  useTicket,
  useCreateTicket,
  useUpdateTicket,
  useUploadTicketAttachment,
  useDeleteTicketAttachment,
} from '@/hooks/useTickets'
import { ticketApi } from '@/services/api'
import { useAssets } from '@/hooks/useAssets'
import type { TicketPriority, TicketCategory, TicketAttachment } from '@/types'

const PRIORITY_OPTIONS: { value: TicketPriority; label: string }[] = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
]

const CATEGORY_OPTIONS: { value: TicketCategory; label: string }[] = [
  { value: 'hardware', label: 'Hardware' },
  { value: 'software', label: 'Software' },
  { value: 'network', label: 'Network' },
  { value: 'personal', label: 'Personal' },
  { value: 'other', label: 'Other' },
]

const MAX_FILE_SIZE_MB = 50
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

const ALLOWED_MIME_TYPES = [
  // Images
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml',
  // Documents
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  // Text
  'text/plain', 'text/csv', 'text/html', 'text/xml',
  // Archives
  'application/zip', 'application/x-rar-compressed', 'application/gzip', 'application/x-7z-compressed',
  // Generic binary (for unknown but valid files)
  'application/octet-stream',
]

/** Get a human-friendly file size string */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Get icon for file type */
function getFileIcon(mimetype: string) {
  if (mimetype.startsWith('image/')) return <Image size={16} className="text-blue-500" />
  if (mimetype.includes('spreadsheet') || mimetype.includes('excel') || mimetype === 'text/csv')
    return <FileSpreadsheet size={16} className="text-green-500" />
  if (mimetype.includes('zip') || mimetype.includes('rar') || mimetype.includes('7z') || mimetype.includes('gzip'))
    return <Archive size={16} className="text-yellow-500" />
  if (mimetype.includes('pdf') || mimetype.includes('word') || mimetype.includes('document') || mimetype.startsWith('text/'))
    return <FileText size={16} className="text-red-500" />
  return <FileIcon size={16} className="text-gray-500" />
}

/** Validate a file for upload */
function validateFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `File "${file.name}" is too large (${formatFileSize(file.size)}). Maximum size is ${MAX_FILE_SIZE_MB}MB.`
  }
  // Allow octet-stream for unknown types — backend will also validate
  if (!ALLOWED_MIME_TYPES.includes(file.type) && file.type !== '') {
    return `File "${file.name}" has an unsupported type (${file.type}).`
  }
  return null
}

export default function TicketForm() {
  const navigate = useNavigate()
  const { ticketId } = useParams<{ ticketId: string }>()
  const [searchParams] = useSearchParams()
  const isEditing = !!ticketId

  const { data: ticketData, isLoading: ticketLoading } = useTicket(ticketId ?? '', { enabled: isEditing })
  const createTicket = useCreateTicket()
  const updateTicket = useUpdateTicket()
  const uploadAttachment = useUploadTicketAttachment()
  const deleteAttachment = useDeleteTicketAttachment()

  // Load assets for the asset dropdown
  const { data: assetsData } = useAssets({ page: 1, limit: 100 })
  const assets = assetsData?.data ?? []

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState<TicketPriority>('normal')
  const [category, setCategory] = useState<TicketCategory>('other')
  const [assetId, setAssetId] = useState<number | null>(null)

  // File upload state
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [existingAttachments, setExistingAttachments] = useState<TicketAttachment[]>([])
  const [dragActive, setDragActive] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Pre-fill from query param or existing ticket
  useEffect(() => {
    if (isEditing && ticketData?.data) {
      const t = ticketData.data
      setTitle(t.title)
      setDescription(t.description ?? '')
      setPriority(t.priority as TicketPriority)
      setCategory((t.category ?? 'other') as TicketCategory)
      setAssetId(t.asset_id ?? null)
      setExistingAttachments(t.attachments ?? [])
    } else if (!isEditing) {
      // Check for asset_id query param (from "Create Ticket" button on asset page)
      const assetIdParam = searchParams.get('asset_id')
      if (assetIdParam) {
        // Find asset by asset_id string (e.g., "AST-0001")
        const found = assets.find((a) => a.asset_id === assetIdParam)
        if (found) {
          setAssetId(found.id)
          // Pre-fill title with asset name
          if (!title) {
            setTitle(`Issue with ${found.name}`)
          }
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- title intentionally excluded; only pre-fill when empty
  }, [isEditing, ticketData, searchParams, assets])

  // File validation and addition
  const addFiles = useCallback((files: File[]) => {
    const errors: string[] = []
    const valid: File[] = []

    for (const file of files) {
      const error = validateFile(file)
      if (error) {
        errors.push(error)
      } else {
        valid.push(file)
      }
    }

    if (errors.length > 0) {
      setUploadError(errors.join(' '))
    }
    if (valid.length > 0) {
      setPendingFiles((prev) => {
        // Filter out duplicates by name+size
        const newFiles = valid.filter((f) => !prev.some((p) => p.name === f.name && p.size === f.size))
        return newFiles.length > 0 ? [...prev, ...newFiles] : prev
      })
    }
  }, [])

  // Drag & drop handlers
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)
    setUploadError(null)

    const files = Array.from(e.dataTransfer.files)
    addFiles(files)
  }, [addFiles])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)
  }, [])

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError(null)
    if (e.target.files) {
      addFiles(Array.from(e.target.files))
    }
    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [addFiles])

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index))
    setUploadError(null)
  }

  const handleDeleteExistingAttachment = async (attachment: TicketAttachment) => {
    if (!ticketId) return
    try {
      await deleteAttachment.mutateAsync({ ticketId, attachmentId: attachment.id })
      setExistingAttachments((prev) => prev.filter((a) => a.id !== attachment.id))
    } catch (error) {
      console.error('Failed to delete attachment:', error)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return

    try {
      setIsUploading(pendingFiles.length > 0)

      if (isEditing && ticketId) {
        // Update ticket metadata
        await updateTicket.mutateAsync({
          ticketId,
          data: {
            title: title.trim(),
            description: description.trim() || undefined,
            priority,
            category,
            asset_id: assetId,
          },
        })

        // Upload any new pending files
        for (const file of pendingFiles) {
          await uploadAttachment.mutateAsync({ ticketId, file })
        }

        navigate(`/tickets/${ticketId}`)
      } else {
        // Create ticket first
        const result = await createTicket.mutateAsync({
          title: title.trim(),
          description: description.trim() || undefined,
          priority,
          category,
          asset_id: assetId ?? undefined,
        })

        const newTicketId = result.data!.ticket_id

        // Upload pending files to the newly created ticket
        for (const file of pendingFiles) {
          try {
            await uploadAttachment.mutateAsync({ ticketId: newTicketId, file })
          } catch (err) {
            console.error(`Failed to upload ${file.name}:`, err)
            // Continue uploading remaining files even if one fails
          }
        }

        navigate(`/tickets/${newTicketId}`)
      }
    } catch (error) {
      console.error('Failed to save ticket:', error)
    } finally {
      setIsUploading(false)
    }
  }

  if (isEditing && ticketLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Loading ticket...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(isEditing ? `/tickets/${ticketId}` : '/tickets')}>
          <ArrowLeft size={20} />
        </Button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {isEditing ? 'Edit Ticket' : 'New Ticket'}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader title={isEditing ? 'Edit Ticket Details' : 'Ticket Details'} />
          <CardContent className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Brief description of the issue"
                required
                maxLength={300}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Detailed description, steps to reproduce, etc."
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 resize-y"
              />
            </div>

            {/* Priority & Category row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Priority
                </label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as TicketPriority)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                >
                  {PRIORITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as TicketCategory)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Linked Asset */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Linked Asset
              </label>
              <select
                value={assetId ?? ''}
                onChange={(e) => setAssetId(e.target.value ? Number(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              >
                <option value="">None</option>
                {assets.map((asset) => (
                  <option key={asset.id} value={asset.id}>
                    {asset.name} ({asset.asset_id})
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Optional. Link this ticket to an IT asset.
              </p>
            </div>

            {/* Attachments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Attachments
              </label>

              {/* Existing attachments (edit mode) */}
              {isEditing && existingAttachments.length > 0 && (
                <div className="mb-3 space-y-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Current attachments:</p>
                  {existingAttachments.map((att) => (
                    <div
                      key={att.id}
                      className="flex items-center gap-3 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                    >
                      {getFileIcon(att.mimetype)}
                      <a
                        href={ticketApi.getAttachmentDownloadUrl(ticketId!, att.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 min-w-0 text-sm text-primary-600 dark:text-primary-400 hover:underline truncate"
                      >
                        {att.filename}
                      </a>
                      <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                        {formatFileSize(att.file_size)}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDeleteExistingAttachment(att)}
                        className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                        title="Remove attachment"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Pending files list */}
              {pendingFiles.length > 0 && (
                <div className="mb-3 space-y-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {isEditing ? 'New files to upload:' : 'Files to upload:'}
                  </p>
                  {pendingFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="flex items-center gap-3 px-3 py-2 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg"
                    >
                      {getFileIcon(file.type)}
                      <span className="flex-1 min-w-0 text-sm text-gray-700 dark:text-gray-300 truncate">
                        {file.name}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                        {formatFileSize(file.size)}
                      </span>
                      <button
                        type="button"
                        onClick={() => removePendingFile(index)}
                        className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                        title="Remove file"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Drop zone */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                className={`flex flex-col items-center justify-center px-4 py-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                  dragActive
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-gray-50 dark:bg-gray-800/50'
                }`}
              >
                <Upload size={24} className={`mb-2 ${dragActive ? 'text-primary-500' : 'text-gray-400 dark:text-gray-500'}`} />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium text-primary-600 dark:text-primary-400">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Images, documents, spreadsheets, PDFs, archives. Max {MAX_FILE_SIZE_MB}MB per file.
                </p>
              </div>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileInputChange}
                className="hidden"
              />

              {/* Upload error */}
              {uploadError && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-2">{uploadError}</p>
              )}
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate(isEditing ? `/tickets/${ticketId}` : '/tickets')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                icon={<Save size={16} />}
                loading={createTicket.isPending || updateTicket.isPending || isUploading}
                disabled={!title.trim()}
              >
                {isUploading
                  ? 'Uploading files...'
                  : isEditing
                  ? 'Save Changes'
                  : pendingFiles.length > 0
                  ? `Create Ticket (${pendingFiles.length} file${pendingFiles.length !== 1 ? 's' : ''})`
                  : 'Create Ticket'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
